import { useContext } from 'react';
import { matchRoutes } from 'react-router-dom';

import { PermissionGroupApp, useGetPermissionCatalogQuery, type TotadminPermission } from 'data/__generated__';
import { PAGES, type PageType } from 'constants/pages';
import { UserContext } from 'contexts/UserContext';

/**
 * Le point de passage UNIQUE pour savoir si l'utilisateur connecté détient un droit fin. Les droits
 * viennent de `effectivePermissions` (union de ses groupes, calculée par l'API) : ne jamais les
 * redéduire des noms de rôle côté front.
 */
export const useHasPermission = (permission: TotadminPermission): boolean => {
    const { effectivePermissions } = useContext(UserContext);

    return effectivePermissions?.includes(permission) ?? false;
};

/** Le filtre commun aux trois consommateurs de `PAGES` : routes, menu, palette. */
export const useCanAccessPage = (): ((page: PageType) => boolean) => {
    const { effectivePermissions } = useContext(UserContext);

    return (page) => canAccessPage(page, effectivePermissions ?? []);
};

/** Le droit de la page et ses `alsoRequires` : la même règle pour la route, le menu et la palette. */
export const canAccessPage = (page: PageType, effectivePermissions: readonly TotadminPermission[]): boolean =>
    page.permission === null ||
    [page.permission, ...(page.alsoRequires ?? [])].every((permission) => effectivePermissions.includes(permission));

const PAGE_ROUTES = Object.values(PAGES).map((page) => ({ path: page.url, page }));

/**
 * La page servie à cette adresse, départagée comme le routeur le fait (`/user/create-toteam` gagne sur
 * `/user/:userId`). `null` pour une adresse hors `PAGES` (accueil) : elle n'est pas gatée.
 */
export const findPageForPath = (pathname: string): PageType | null =>
    matchRoutes(PAGE_ROUTES, pathname)?.[0]?.route.page ?? null;

export type PermissionGate = {
    isAllowed: boolean;
    /** Les droits requis que l'utilisateur ne détient pas — vide quand `isAllowed`. */
    missing: readonly TotadminPermission[];
};

/**
 * Le verrou déclaratif des sections et des boutons : il faut détenir TOUS les droits listés. Lecture pure
 * du `UserContext`, SANS Apollo : `Button` passe par ici, et `confirmPopup` le monte dans une racine React
 * à part, sans `ApolloProvider` — un `useQuery`, même en `skip`, y lève et le dialogue ne s'affiche
 * jamais. Le libellé de ce qui manque vient de `useMissingPermissionsReason`, dans le rendu verrouillé.
 */
export const usePermissionGate = (requiredPermissions?: readonly TotadminPermission[]): PermissionGate => {
    const { effectivePermissions } = useContext(UserContext);
    const missing = (requiredPermissions ?? []).filter((permission) => !effectivePermissions?.includes(permission));

    return { isAllowed: missing.length === 0, missing };
};

/**
 * « Droit requis : Consulter : Gammes de prix » — pour l'infobulle d'un bouton ou le bandeau d'une section.
 * Les libellés viennent du catalogue de l'API, chargé seulement quand un droit manque vraiment (une seule
 * fois, cache Apollo) — un utilisateur Toteam ne le déclenche jamais. `null` quand rien ne manque.
 */
export const useMissingPermissionsReason = (missing: readonly TotadminPermission[]): string | null => {
    const { data } = useGetPermissionCatalogQuery({
        variables: { app: PermissionGroupApp.Totadmin },
        skip: missing.length === 0,
    });

    if (missing.length === 0) {
        return null;
    }

    const labelOf = (code: string) => data?.permissionCatalog.find((entry) => entry.code === code)?.label ?? code;

    return `${missing.length > 1 ? 'Droits requis' : 'Droit requis'} : ${missing.map(labelOf).join(', ')}`;
};
